import {
  HotspotPinComponent,
  ImagePinComponent,
  LinePinComponent,
  Pin,
  PinComponent,
  QuizPin,
  OpenQuestionPin,
  WordCloudPin,
  QuizPinAnswer,
  QuizTeacherPin,
  ShapePinComponent,
  SlidePin,
  TextPinComponent,
  AnswerAreaPinComponent,
  UneditableShapePinComponent,
  QuizPinAnswerText,
  QuizPinAnswerImage,
  TeacherPin,
  VideoPinComponent,
  SpinnerPinComponent,
  UnsupportedPinComponent,
  UnsupportedPin,
  TimerPinComponent,
  FillInTheBlanksAreaPinComponent,
  CategoryAreaPinComponent,
  AnswerByCategoryPin,
  SortingQuestionPin,
  FillInTheBlanksPin,
  PollPin,
  DragAndDropPin,
  DropZonePinComponent,
  AudioPinComponent,
  DragAndDropLinkedComponent,
} from '../../models';

export function isSlidePin(pin: Pin): pin is SlidePin {
  return pin.type === 'SLIDE';
}

export function isQuizPin(pin: Pin): pin is QuizPin {
  return pin.type === 'MULTIPLE_CHOICE';
}

export function isOpenQuestionPin(pin: Pin): pin is OpenQuestionPin {
  return pin.type === 'OPEN_QUESTION';
}

export function isWordCloudPin(pin: Pin): pin is WordCloudPin {
  return pin.type === 'WORD_CLOUD';
}

export function isPollPin(pin: Pin): pin is PollPin {
  return pin.type === 'POLL';
}

export function isAnswerByCategoryPin(pin: Pin): pin is AnswerByCategoryPin {
  return pin.type === 'ANSWER_BY_CATEGORY';
}

export function isSortingQuestionPin(pin: Pin): pin is SortingQuestionPin {
  return pin.type === 'SORTING_QUESTION';
}

export function isFillInTheBlanksPin(pin: Pin): pin is FillInTheBlanksPin {
  return pin.type === 'FILL_IN_THE_BLANKS';
}

export function isUnsupportedPin(pin: Pin): pin is UnsupportedPin {
  return pin.type === 'UNSUPPORTED';
}

export function isDragAndDropPin(pin: Pin): pin is DragAndDropPin {
  return pin.type === 'DRAG_AND_DROP';
}

export type InteractivePin =
  | QuizPin
  | OpenQuestionPin
  | WordCloudPin
  | PollPin
  | AnswerByCategoryPin
  | SortingQuestionPin
  | FillInTheBlanksPin
  | DragAndDropPin;

export function isInteractiveQuestionPin(pin: Pin): pin is InteractivePin {
  return (
    isQuizPin(pin) ||
    isOpenQuestionPin(pin) ||
    isWordCloudPin(pin) ||
    isPollPin(pin) ||
    isAnswerByCategoryPin(pin) ||
    isSortingQuestionPin(pin) ||
    isFillInTheBlanksPin(pin) ||
    isDragAndDropPin(pin)
  );
}

export function isInteractiveQuestionPinTextPinComponent(pin: Pin, pinComponent: PinComponent): boolean {
  if (isInteractiveQuestionPin(pin) && isTextPinComponent(pinComponent)) {
    return pin.settings.questionId === pinComponent.id;
  }

  return false;
}

export function isQuizTeacherPin(pin: Pin): pin is QuizTeacherPin {
  return isTeacherPin(pin) && isQuizPin(pin);
}

export function isLinePinComponent(pinComponent: PinComponent): pinComponent is LinePinComponent {
  return pinComponent.type === 'LINE';
}

export function isHotspotPinComponent(pinComponent: PinComponent): pinComponent is HotspotPinComponent {
  return pinComponent.type === 'HOTSPOT';
}

export function isAudioPinComponent(pinComponent: PinComponent): pinComponent is AudioPinComponent {
  return pinComponent.type === 'AUDIO';
}

export function isImagePinComponent(pinComponent: PinComponent): pinComponent is ImagePinComponent {
  return pinComponent.type === 'IMAGE';
}

export function isShapePinComponent(pinComponent: PinComponent): pinComponent is ShapePinComponent {
  return pinComponent.type === 'SHAPE';
}

export function isTextPinComponent(pinComponent: PinComponent): pinComponent is TextPinComponent {
  return pinComponent.type === 'TEXT';
}

export function IsVideoPinComponent(pinComponent: PinComponent): pinComponent is VideoPinComponent {
  return pinComponent.type === 'VIDEO';
}

export function isAnswerAreaPinComponent(pinComponent: PinComponent): pinComponent is AnswerAreaPinComponent {
  return pinComponent.type === 'ANSWER_AREA';
}

export function isQuizPinAnswerText(answer: QuizPinAnswer): answer is QuizPinAnswerText {
  return answer.type === 'TEXT';
}

export function isQuizPinAnswerImage(answer: QuizPinAnswer): answer is QuizPinAnswerImage {
  return answer.type === 'IMAGE';
}

export function isUneditableShapePinComponent(pinComponent: PinComponent): pinComponent is UneditableShapePinComponent {
  return pinComponent.type === 'UNEDITABLE_SHAPE';
}

export function isTeacherPin(pin: Pin): pin is TeacherPin {
  return 'teacherSettings' in pin;
}

export function isSpinnerPinComponent(pinComponent: PinComponent): pinComponent is SpinnerPinComponent {
  return pinComponent.type === 'SPINNER';
}

export function isTimerPinComponent(pinComponent: PinComponent): pinComponent is TimerPinComponent {
  return pinComponent.type === 'TIMER';
}

export function isFillInTheBlanksAreaPinComponent(
  pinComponent: PinComponent
): pinComponent is FillInTheBlanksAreaPinComponent {
  return pinComponent.type === 'FILL_IN_THE_BLANKS_AREA';
}

export function isCategoryAreaPinComponent(pinComponent: PinComponent): pinComponent is CategoryAreaPinComponent {
  return pinComponent.type === 'CATEGORY_AREA';
}

export function isDropZonePinComponent(pinComponent: PinComponent): pinComponent is DropZonePinComponent {
  return pinComponent.type === 'DROP_ZONE';
}

export function isDragAndDropItemPinComponent(
  pinComponent: PinComponent,
  pinSettingsLinkedPinComponents: DragAndDropLinkedComponent
): pinComponent is ImagePinComponent | HotspotPinComponent | TextPinComponent | ShapePinComponent {
  return (
    (isImagePinComponent(pinComponent) ||
      isHotspotPinComponent(pinComponent) ||
      isTextPinComponent(pinComponent) ||
      isShapePinComponent(pinComponent)) &&
    Object.keys(pinSettingsLinkedPinComponents).includes(pinComponent.id)
  );
}

export function isUnsupportedPinComponent(pinComponent: PinComponent): pinComponent is UnsupportedPinComponent {
  return pinComponent.type === 'UNSUPPORTED';
}
